import { useEffect, useState } from 'react';
import Spinner from 'react-activity/dist/Spinner';
import styled from 'styled-components';
import { useModal } from '../../components/UI';
import { validateAccessGranted } from '../../utils/accessGrantedValidator';
import message from '../../utils/Message';
import ArrowIcon from './assets/arrowRight.svg';

import AddEditGroup from './components/AddEditGroup';
import AddEditGroupCategory from './components/AddEditGroupCategory';
import AddPatientsToGroup from './components/AddPatientsToGroup';
import GroupList from './components/GroupList';
import PatientList from './components/PatientList';
import { Group, GroupCategory } from './components/types';
import useCategories from './components/useCategories';
import useGroups from './components/useGroups';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const Title = styled.p`
  color: #0a313f;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 35px;
`;

const Items = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 15px;
  align-items: flex-start;
  flex-grow: 1;
  overflow: hidden;

  & > div {
    flex: 1;
  }

  .ui-group-container {
    max-width: 30%;
  }
`;

const ArrowRight = styled.img`
  margin-top: 50px;
`;

const ContainerStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  border: '1px solid #EBF2FE',
  boxShadow: 'none',
};

const UserGroupsPermissions = () => {
  const [isLoading, setIsLoading] = useState(false);

  const { categories, getCategories, isLoading: isLoadingCategories, loadMore: loadMoreCategories, hasMore: hasMoreCategories, total: totalCategories } = useCategories();

  const { groups, getGroupsByCategoryId, patientsPerGroup, isLoading: isGroupsLoading, removePatientFromGroup, getPatientsByGroupId, loadMoreGroups, hasMoreGroups } = useGroups();

  const hasAccessToGroups = validateAccessGranted('/groups', false);

  useEffect(() => {
    getCategories();
  }, []);

  const { isOpen: isOpenGroupCategory, openModal: openGroupCategoryModal, closeModal: closeGroupCategoryModal } = useModal();

  const { isOpen: isOpenGroup, openModal: openGroupModal, closeModal: closeGroupModal } = useModal();

  const { isOpen: isOpenAddPatientsToGroup, openModal: openAddPatientsToGroupModal, closeModal: closeAddPatientsToGroupModal } = useModal();

  const [selectedGroupCategory, setSelectedGroupCategory] = useState<GroupCategory | null>();

  const [selectedGroup, setSelectedGroup] = useState<Group | null>();

  // Edit
  const [activeGroupCategory, setActiveGroupCategory] = useState<GroupCategory | null>(null);
  const [activeGroup, setActiveGroup] = useState<Group | null>(null);

  useEffect(() => {
    getGroups();
  }, [selectedGroupCategory]);

  useEffect(() => {
    if (selectedGroup?.id) {
      getPatientsByGroupId(selectedGroup?.id);
    }
  }, [selectedGroup]);

  // Group Category
  const openAddGroupCategory = () => {
    setActiveGroupCategory(null);
    openGroupCategoryModal();
  };

  const openEditGroupCategory = (category: GroupCategory) => {
    setActiveGroupCategory(category);
    openGroupCategoryModal();
  };

  const onCloseGroupCategory = () => {
    getCategories();
    closeGroupCategoryModal();
  };

  // Group
  const openAddGroup = () => {
    setActiveGroup(null);
    openGroupModal();
  };

  const openEditGroup = (category: Group) => {
    setActiveGroup(category);
    openGroupModal();
  };

  const onCloseGroup = () => {
    getGroups();
    closeGroupModal();
  };

  const getGroups = () => {
    if (selectedGroupCategory) {
      console.log('selectedGroupCategory id', selectedGroupCategory.id);
      getGroupsByCategoryId(selectedGroupCategory.id);
    } else {
      setSelectedGroup(null);
    }
  };

  const handleSelectionOfGroupCategory = (item: GroupCategory) => {
    setSelectedGroupCategory(item);
    setSelectedGroup(null);
  };

  const handleCloseAddPatientsToGroupModal = () => {
    if (selectedGroup) {
      getPatientsByGroupId(selectedGroup?.id);
    }

    closeAddPatientsToGroupModal();
  };

  const handleRemovePatientFromGroup = (patientId: string) => {
    if (selectedGroup) {
      removePatientFromGroup([patientId], selectedGroup.id)
        .then(() => {
          message.show('Client removed from group successfully.');
          getPatientsByGroupId(selectedGroup?.id);
        })
        .catch(() => {
          message.show('Sorry, There was a problem removing client.');
        });
    }
  };

  console.log('hasAccessToGroups', hasAccessToGroups);

  const renderGroupCategories = () => {
    return (
      <Items>
        <GroupList
          title="Group Categories"
          data={categories}
          onItemClick={item => handleSelectionOfGroupCategory(item)}
          selectedGroup={selectedGroupCategory}
          buttonOnClick={openAddGroupCategory}
          onActionClick={item => openEditGroupCategory(item)}
          isVisible={hasAccessToGroups && !isLoadingCategories}
          isEditable={hasAccessToGroups}
          buttonLabel={'Assign Additional Categories'}
          isLoading={isLoadingCategories}
          loadMore={loadMoreCategories}
          hasMore={hasMoreCategories}
          total={totalCategories}
          containerStyle={ContainerStyle}
        />
        {selectedGroupCategory && (
          <>
            <ArrowRight src={ArrowIcon} alt="Category" />
            <GroupList
              title="Groups"
              data={groups}
              isLoading={isGroupsLoading}
              onItemClick={item => setSelectedGroup(item)}
              selectedGroup={selectedGroup}
              buttonOnClick={openAddGroup}
              isVisible={hasAccessToGroups && !isLoadingCategories}
              isEditable={hasAccessToGroups}
              onActionClick={item => openEditGroup(item)}
              buttonLabel={selectedGroupCategory?.name && 'Assign Additional Groups'}
              loadMore={() => loadMoreGroups(selectedGroupCategory.id)}
              hasMore={hasMoreGroups}
              containerStyle={ContainerStyle}
            />
          </>
        )}

        {selectedGroup && (
          <>
            <ArrowRight src={ArrowIcon} alt="Category" />
            <PatientList title={''} group={selectedGroup} buttonOnClick={openAddPatientsToGroupModal} patients={patientsPerGroup} containerStyle={ContainerStyle} />
          </>
        )}
      </Items>
    );
  };

  const renderContent = () => {
    if (isLoading && !categories) {
      return (
        <LoaderContainer>
          <Spinner color="#2096F3" size={34} speed={0.6} animating={isLoading} />
        </LoaderContainer>
      );
    }

    return renderGroupCategories();
  };

  return (
    <Container>
      {renderContent()}
      <AddEditGroupCategory closeModal={onCloseGroupCategory} isOpen={isOpenGroupCategory} category={activeGroupCategory} />
      <AddEditGroup closeModal={onCloseGroup} isOpen={isOpenGroup} category={selectedGroupCategory} group={activeGroup} />
      <AddPatientsToGroup closeModal={handleCloseAddPatientsToGroupModal} isOpen={isOpenAddPatientsToGroup} group={selectedGroup} />
    </Container>
  );
};

export default UserGroupsPermissions;
